import { ReactNode, useEffect, useRef } from "react"
import { useAccount, useDisconnect } from "wagmi"
import { useQuery } from "@tanstack/react-query"
import { Box } from "@mui/system"
import { Typography } from "@mui/material"

// Address screening goes through the euler-lite server proxy, which holds
// the compliance API key (this SPA has no server of its own). The screening
// vendor is an upstream implementation detail — this app only sees the
// neutral `{ addressIsSuspicious }` contract. The VPN flag is derived
// server-side from edge-set headers, so the body only carries the address.
const SCREENING_ENDPOINT =
  import.meta.env.VITE_SCREENING_URI ??
  "https://app.euler.finance/api/internal/screen-address"

const SCREENING_TIMEOUT_MS = 15_000

// Within one wallet connection a clean verdict is trusted for a bounded
// window; once it expires, the refetch gate below withholds actionable
// children until the fresh verdict lands.
const SCREENING_STALE_MS = 5 * 60_000

async function checkAddress(
  address: string,
): Promise<{ addressIsSuspicious: boolean }> {
  const response = await fetch(SCREENING_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ address }),
    signal: AbortSignal.timeout(SCREENING_TIMEOUT_MS),
  })

  if (!response.ok) {
    throw new Error(`Screening check failed: ${response.status}`)
  }

  return response.json()
}

// Screening state is derived from the query for the *current* address —
// there is no sticky flag, so switching accounts can neither inherit a
// previous block nor be blocked by a stale in-flight result: the query is
// keyed by address and re-evaluates on every change.
const ScreeningProvider = ({ children }: { children: ReactNode }) => {
  const { address } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const disconnectRequestedFor = useRef<string | undefined>(undefined)

  // Each transition to a (new or reconnected) address is a distinct
  // connection: bump synchronously during render so the very first query for
  // it already carries the fresh key — a reconnect always re-screens instead
  // of reusing a cached clearance.
  const connectionRef = useRef<{ address?: string; id: number }>({ id: 0 })
  if (address !== connectionRef.current.address) {
    connectionRef.current = { address, id: connectionRef.current.id + 1 }
  }
  const connectionId = connectionRef.current.id

  const { data, isError, isFetching } = useQuery({
    queryKey: ["address-screening", address, connectionId],
    queryFn: () => checkAddress(address!),
    enabled: !!address,
    staleTime: SCREENING_STALE_MS,
    refetchOnWindowFocus: false,
    retry: 2,
  })

  // Fail-closed: a failed or timed-out screening call blocks exactly like a
  // flagged address, and only an explicit `false` verdict clears it.
  const suspicious =
    isError || (data !== undefined && data.addressIsSuspicious !== false)
  // Clearance requires an explicit clean verdict that is NOT currently being
  // revalidated — stale clean data must not keep authorizing while the
  // mandatory refresh is in flight.
  const cleared =
    !!address &&
    !isFetching &&
    data !== undefined &&
    data.addressIsSuspicious === false

  useEffect(() => {
    if (!address || !suspicious) return
    if (disconnectRequestedFor.current === address) return
    disconnectRequestedFor.current = address
    // The blocked UI below does not depend on this succeeding — a rejected
    // disconnect must not leave the app usable.
    disconnectAsync().catch(() => {})
  }, [address, suspicious, disconnectAsync])

  // No wallet connected: nothing to screen.
  if (!address) return <>{children}</>

  if (suspicious) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
        p={4}
      >
        <Typography fontWeight="bold" variant="subtitle1">
          Something went wrong
        </Typography>
        <Typography color="text.secondary" textAlign="center" variant="body1">
          An unknown error occurred. Please try again later.
        </Typography>
      </Box>
    )
  }

  // Pending is not clearance: no actionable flow is exposed before an
  // explicit clean verdict for this address.
  if (!cleared) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
        gap={2}
        p={4}
      >
        <Typography color="text.secondary" textAlign="center" variant="body1">
          Verifying wallet address…
        </Typography>
      </Box>
    )
  }

  return <>{children}</>
}

export default ScreeningProvider
